import type { ComponentPropsWithRef, MouseEvent, ReactElement, ReactNode } from 'react';
import { iconButtonClassName } from './buttonStyles';
import type { ButtonVariant } from './buttonStyles';
import type { PopoverSide } from './positioning';
import { Spinner } from './Spinner';
import { Tooltip } from './Tooltip';

export interface IconButtonProps extends Omit<
  ComponentPropsWithRef<'button'>,
  'children' | 'aria-label'
> {
  /** Required: it is both the accessible name and the tooltip text. */
  label: string;
  icon: ReactNode;
  variant?: ButtonVariant;
  /** Allows a 36px target on precise pointers. Touch always gets 44px. */
  compact?: boolean;
  /** Renders `aria-pressed` — for toggles such as "show grid". */
  pressed?: boolean;
  loading?: boolean;
  tooltip?: boolean;
  tooltipSide?: PopoverSide;
}

export function IconButton({
  label,
  icon,
  variant = 'ghost',
  compact = false,
  pressed,
  loading = false,
  tooltip = true,
  tooltipSide = 'bottom',
  disabled = false,
  className,
  onClick,
  type = 'button',
  ...rest
}: IconButtonProps): ReactElement {
  const inert = disabled || loading;

  function handleClick(event: MouseEvent<HTMLButtonElement>): void {
    if (inert) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onClick?.(event);
  }

  const button = (
    <button
      {...rest}
      type={type}
      aria-label={label}
      aria-pressed={pressed}
      aria-disabled={inert || undefined}
      aria-busy={loading || undefined}
      onClick={handleClick}
      className={iconButtonClassName({
        variant,
        compact,
        pressed: pressed === true,
        disabled: inert,
        className,
      })}
    >
      {loading ? (
        <Spinner size="sm" label={null} />
      ) : (
        <span aria-hidden="true" className="flex items-center [&>svg]:h-[18px] [&>svg]:w-[18px]">
          {icon}
        </span>
      )}
    </button>
  );

  if (!tooltip) return button;

  return (
    <Tooltip label={label} side={tooltipSide}>
      {button}
    </Tooltip>
  );
}
